import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, UtensilsCrossed, ArrowRight } from "lucide-react";

export const ChatWidget = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  // Seed / refresh the welcome message when opening or switching language (only if no real conversation yet)
  useEffect(() => {
    setMessages((prev) => {
      const hasUser = prev.some((m) => m.role === "user");
      if (!hasUser) return [{ role: "assistant", content: t("chat.welcome") }];
      return prev;
    });
  }, [lang]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t("chat.welcome") }]);
    }
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    const history = messages.filter((m) => m.role !== "system");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setInput("");
    setLoading(true);
    try {
      const res = await api.post("/chat", {
        message: text,
        history: history.slice(-6),
        lang,
        session_id: sessionId,
      });
      setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: "assistant", content: res.data.reply, suggestions: res.data.suggestions || [] }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: t("chat.error") }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button
        data-testid="chat-launcher"
        onClick={() => setOpen((o) => !o)}
        aria-label={t("chat.openLabel")}
        className="fixed bottom-6 right-6 z-[60] w-14 h-14 rounded-full bg-[#E63946] text-white flex items-center justify-center shadow-[0_8px_24px_rgba(230,57,70,0.45)] hover:scale-105 transition-transform"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            data-testid="chat-panel"
            className="fixed bottom-24 right-6 z-[60] w-[calc(100vw-3rem)] max-w-[380px] h-[540px] max-h-[70vh] bg-[#FDFBF7] border border-[#E5E0D8] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#2B2B2B] text-[#FDFBF7] px-5 py-4 flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-[#E63946] flex items-center justify-center">
                <UtensilsCrossed className="w-5 h-5" />
              </span>
              <div>
                <p className="font-display font-bold leading-tight">{t("chat.title")}</p>
                <p className="text-xs text-[#FDFBF7]/70 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#4ADE80] inline-block" /> {t("chat.subtitle")}
                </p>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto p-4 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.role === "user" ? "items-end" : "items-start"}`}>
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#E63946] text-white rounded-br-sm"
                        : "bg-white border border-[#E5E0D8] text-[#2B2B2B] rounded-bl-sm"
                    }`}
                  >
                    {m.content}
                  </div>
                  {m.role === "assistant" && m.suggestions && m.suggestions.length > 0 && (
                    <div className="mt-2 flex flex-col gap-1.5 max-w-[85%]">
                      {m.suggestions.map((s) => (
                        <button
                          key={s.id}
                          data-testid={`chat-suggestion-${s.id}`}
                          onClick={() => { setOpen(false); navigate(`/restaurant/${s.id}`); }}
                          className="group flex items-center justify-between gap-2 text-left bg-[#F4EFE6] hover:bg-[#E63946] hover:text-white border border-[#E5E0D8] rounded-xl px-3 py-2 transition-colors"
                        >
                          <span className="min-w-0">
                            <span className="block text-sm font-bold truncate">{s.name}</span>
                            <span className="block text-xs font-mono opacity-70 truncate">{s.cuisine}{s.city ? ` · ${s.city}` : ""}</span>
                          </span>
                          <ArrowRight className="w-4 h-4 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {loading && (
                <div className="flex justify-start">
                  <div className="bg-white border border-[#E5E0D8] rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1">
                    <span className="w-2 h-2 bg-[#9C9C9C] rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-[#9C9C9C] rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-[#9C9C9C] rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <form onSubmit={send} className="p-3 border-t border-[#E5E0D8] flex items-center gap-2">
              <input
                data-testid="chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder={t("chat.placeholder")}
                className="flex-1 h-11 px-4 rounded-full bg-white border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors text-sm"
              />
              <button
                data-testid="chat-send-btn"
                type="submit"
                disabled={loading || !input.trim()}
                className="w-11 h-11 rounded-full bg-[#E63946] text-white flex items-center justify-center hover:scale-105 transition-transform disabled:opacity-40"
              >
                <Send className="w-5 h-5" />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
